import { PillLabel } from "./pill-label"
import { useId, type CSSProperties, type ReactNode } from "react"
import { cn } from "@/lib/utils"

type Option<T extends string> = { value: T; label: ReactNode }

/** A single, mutually exclusive choice. Native radios provide arrow-key navigation. */
export function SegmentedControl<T extends string>({
  value,
  onValueChange,
  options,
  label,
  className,
}: {
  value: T
  onValueChange: (value: T) => void
  options: readonly Option<T>[]
  label: string
  className?: string
}) {
  const name = useId()
  const active = Math.max(
    0,
    options.findIndex((option) => option.value === value)
  )
  if (options.length < 2) return null
  return (
    <div
      role="radiogroup"
      aria-label={label}
      className={cn("rovero-segments", className)}
      style={{ "--segments": options.length } as CSSProperties}
    >
      <span
        aria-hidden="true"
        className="rovero-segment-indicator"
        style={{ transform: `translateX(${active * 100}%)` }}
      />
      {options.map((option) => (
        <label
          key={option.value}
          className="rovero-segment"
          data-active={option.value === value}
        >
          <input
            className="sr-only"
            type="radio"
            name={name}
            value={option.value}
            checked={option.value === value}
            onChange={() => onValueChange(option.value)}
          />
          <span>
            {typeof option.label === "string" ? (
              <PillLabel>{option.label}</PillLabel>
            ) : (
              option.label
            )}
          </span>
        </label>
      ))}
    </div>
  )
}
